import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Injector, input, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

@Component({
  selector: 'ui-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);

  /** Placeholder text */
  placeholder = input<string>('');

  /** Compact mode: smaller size, role-picker style — ideal for filters */
  compact = input<boolean>(false);

  /** Optional string shown as a suffix (e.g., domain name like @skillztank.com) */
  suffixText = input<string>('');

  /** Optional string shown as a prefix (e.g., protocol like https://) */
  prefixText = input<string>('');

  /** Optional icon name shown as a prefix (Material/SVG icon name) */
  prefixIcon = input<'' | 'search' | 'filter' | 'lock' | 'at'>('');

  /** Input type */
  type = input<string>('text');

  /** Autocomplete behavior for the input */
  autocomplete = input<string>('on');

  /** Accessible label for screen readers (used when no visible label) */
  ariaLabel = input<string>('');

  /** If true, automatically focuses the input when rendered */
  autoFocus = input<boolean>(false);

  /** Emits on every value change */
  valueChange = output<string>();

  /** Unique ID for the input element */
  readonly inputId = `skz-input-${InputComponent._nextId++}`;
  private static _nextId = 0;

  /** Whether the control is currently invalid (for aria-invalid) */
  get isInvalid(): boolean {
    const ctrl = this.injector.get(NgControl, null);
    return !!ctrl?.control?.invalid && !!ctrl?.control?.touched;
  }

  /** Whether to format the input strictly as a URL slug (lowercase, alphanumeric, hyphens) */
  formatAsSlug = input<boolean>(false);

  // ── Internal state ──────────────────────────────────────────────────────

  protected _value = '';
  protected _disabled = false;

  onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;

    if (this.formatAsSlug()) {
      // Keep cursor position stable during replacement
      const cursorStart = inputElement.selectionStart;
      const cursorEnd = inputElement.selectionEnd;

      // Lowercase, replace spaces with hyphens, remove invalid characters
      const formattedValue = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-'); // Prevent multiple consecutive hyphens

      if (value !== formattedValue) {
        value = formattedValue;
        inputElement.value = formattedValue;

        // Restore cursor position if possible
        if (cursorStart !== null && cursorEnd !== null) {
          // Approximate the cursor position after format
          inputElement.setSelectionRange(cursorStart, cursorEnd);
        }
      }
    }

    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string): void {
    this._value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.cdr.markForCheck();
  }

  focus(): void {
    const inputEl = document.getElementById(this.inputId);
    if (inputEl) {
      inputEl.focus();
    }
  }
}
